<template>
  <div v-if="activeDrawers.length" class="sidebar__wrapper">
    <aside class="sidebar__container" aria-modal="true" role="modal" v-focus-trap="isChildDrawerOpen">
      <div class="sidebar__header">
        <h5 class="sidebar__title">{{ currentDrawer.heading }}</h5>
        <button class="sidebar__close" aria-label="close" @click="closeDrawer">
          <svg class="sidebar__close-icon">
            <use href="#close"></use>
          </svg>
        </button>
      </div>
      <div class="sidebar__content">
        <component
          :is="currentDrawer.renderComponent"
          v-bind="currentDrawer.props"
          @heading="drawerHeadingFn($event)"
          @component-updated="handleChildComponentUpdated"
          @[currentDrawer.event?.name]="currentDrawer.event"
        />
      </div>
    </aside>
  </div>
</template>

<script setup>
import { computed, reactive, onBeforeMount, ref } from 'vue';
import QuickView from '../quick-view/app.vue';
import QuickView2026 from '../quick-view-2026/app.vue';
import SizeGuide from '../size-guide/app.vue';
import SizeGuideNew from '../size-guide-new/app.vue';
import BISModal from '../pdp-first-fold/components/product-bis.vue';
import TradeIn from '../trade-in/app.vue';
import EditCartItem from '../cart/components/edit-cart-item.vue';
import ViewSelectionsBYK from '../collection-build-your-kit/components/view-selections-drawer.vue';

const isChildDrawerOpen = ref(false);
const activeDrawers = ref([]); // Stack to manage active drawers
const isClosingDrawer = ref(false);
const overlay = new window.Overlay();
const QUICK_VIEW_2026_CLOSE_DURATION = 280;

const drawerComponents = reactive([
  {
    componentName: 'QuickView',
    renderComponent: QuickView,
    drawerPosition: 'right',
    mWidth: '32rem',
    dWidth: '40rem',
    heading: ''
  },
  {
    componentName: 'QuickView2026',
    renderComponent: QuickView2026,
    drawerPosition: 'right',
    mWidth: '32rem',
    dWidth: '96rem',
    heading: ''
  },
  {
    componentName: 'SizeGuide',
    renderComponent: SizeGuide,
    drawerPosition: 'right',
    mWidth: '28rem',
    dWidth: '36.8rem',
    heading: ''
  },
  {
    componentName: 'SizeGuideNew',
    renderComponent: SizeGuideNew,
    drawerPosition: 'right',
    mWidth: '32.5rem',
    dWidth: '36.8rem',
    heading: ''
  },
  {
    componentName: 'BISModal',
    renderComponent: BISModal,
    drawerPosition: 'right',
    mWidth: '28rem',
    dWidth: '40rem',
    heading: ''
  },
  {
    componentName: 'TradeIn',
    renderComponent: TradeIn,
    drawerPosition: 'right',
    mWidth: '28rem',
    dWidth: '40rem',
    heading: ''
  },
  {
    componentName: 'EditCartItem',
    renderComponent: EditCartItem,
    drawerPosition: 'right',
    mWidth: '32rem',
    dWidth: '40rem',
    heading: ''
  },
  {
    componentName: 'ViewSelectionsBYK',
    renderComponent: ViewSelectionsBYK,
    drawerPosition: 'right',
    mWidth: '30.6rem',
    dWidth: '40rem',
    heading: ''
  },
]);

onBeforeMount(() => {
  window.drawerComponents = drawerComponents;
  window.toggleDrawer = toggleDrawer;
});

const currentDrawer = computed(() => {
  return activeDrawers.value[activeDrawers.value.length - 1] || null;
});

const drawerHeadingFn = (value) => {
  currentDrawer.value.heading = value;
};

const handleChildComponentUpdated = () => {
  isChildDrawerOpen.value = true;
};

const toggleDrawer = (value, drawerName, props, event) => {
  let sideBar = document.querySelector('#js_sidebar');
  let bodyStyle = document.body.style;
  let drawerComponent = drawerComponents.find(item => item.componentName === drawerName);

  const cartChildDrawers = ['EditCartItem', 'BISModal'];

  if (value) {
    if (activeDrawers.value.length === 0) {
      window.mobileNavigation?.close?.();
      if (!cartChildDrawers.includes(drawerName)) {
        window.openCart?.({ open: false });
      }

      activeDrawers.value.push({ ...drawerComponent, props, event, overlay });
      sideBar.classList.add('sidebar--active', 
        drawerComponent.drawerPosition === 'left' ? 'sidebar--left' : 'sidebar--right', 
        drawerName
      );
      sideBar.setAttribute('aria-hidden', false);

      overlay.show({ elClass: drawerName });
      bodyStyle.overflow = 'hidden';
    } else {
      // If another drawer is open, just push the new one
      activeDrawers.value.push({ ...drawerComponent, props, event });
    }

    // Update styles for the newly opened drawer
    sideBar.style.setProperty("--drawer-m-width", drawerComponent.mWidth);
    sideBar.style.setProperty("--drawer-d-width", drawerComponent.dWidth);
    sideBar.classList.add(drawerName);

  } else {
    // If closing the current drawer
    closeDrawer();
  }
};

const wait = (delay) => new Promise((resolve) => {
  window.setTimeout(resolve, delay);
});

const closeDrawer = async () => {
  if (isClosingDrawer.value) return;

  let sideBar = document.querySelector('#js_sidebar');
  let bodyStyle = document.body.style;
  const drawerToClose = activeDrawers.value[activeDrawers.value.length - 1];

  if (!drawerToClose) return;

  if (drawerToClose.componentName === 'QuickView2026') {
    isClosingDrawer.value = true;
    sideBar.classList.add('sidebar--closing');
    await wait(QUICK_VIEW_2026_CLOSE_DURATION);
  }

  // Remove the last drawer from the stack
  const inactivatedDrawer = activeDrawers.value.pop();

  if (inactivatedDrawer) {  
    const { overlay } = inactivatedDrawer;
    sideBar.classList.remove('sidebar--closing');
    sideBar.classList.remove(inactivatedDrawer.componentName);
    overlay?.hide({ elClass: inactivatedDrawer.componentName });

    if (activeDrawers.value.length === 0) {
      // If no more drawers are open, reset the styles
      sideBar.classList.remove('sidebar--active', 'sidebar--left', 'sidebar--right');
      sideBar.setAttribute('aria-hidden', true);
      bodyStyle.overflow = 'auto';
      isChildDrawerOpen.value = false;
    } else {
      // Update sidebar styles for the current drawer
      const currentDrawerComponent = currentDrawer.value;
      sideBar.style.setProperty("--drawer-m-width", currentDrawerComponent.mWidth);
      sideBar.style.setProperty("--drawer-d-width", currentDrawerComponent.dWidth);
      currentDrawerComponent?.overlay?.show({ elClass: currentDrawerComponent.componentName });
      bodyStyle.overflow = 'hidden';
    }

    isClosingDrawer.value = false;

    // // Cleanup: Remove overlay element from DOM
    // if (overlay?.$el) {
    //   document.body.removeChild(overlay.$el);
    //   overlay.$el = null; // Reset the reference
    // }
  }
};

overlay.on('cancel', closeDrawer);

</script>
